import { type B2bPrice, type B2bProduct, listB2bPrices, listB2bProducts, type PriceCsvResult, removeB2bPrice, setB2bPricesOffered, uploadB2bPrices, upsertB2bPrices } from '@api/b2b';
import { DataSheet, type DataSheetColumn } from '@components/ui/DataSheet';
import type { FilterParam, FilterValue } from '@components/ui/EntityFilterDropdown';
import { Button, FileButton, NumberInput, Popover, Switch } from '@mantine/core';
import { ButtonVariant } from '@providers/Mantine/mantineTheme';
import { downloadCsv } from '@utils/csv-download';
import { useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useMemo, useState } from 'react';
import styles from './b2b.module.css';
import { ProductCell } from './shared';
import { errorMessage, formatDay, matchesProductFilter, money, type PriceFallback, productFilterParams } from './utils';


interface PriceRow extends B2bProduct {
    current: B2bPrice | null;
    /** What this list falls back to when it sets no price: the next list's price and its name. */
    inherited: { price: number; from: string } | null;
}

/**
 * One price list: the default, a segment's, or one customer's. Every active SKU is listed;
 * typing a price and saving writes only the rows that changed, and the old price goes into the
 * row's history (with offered on/off and who set each state). Clearing keeps the row and its
 * history. A blank price means the SKU falls back to the next list (customer, segment,
 * default), whose price shows greyed. Customer and segment lists also carry the Offered switch:
 * off takes the product away from that customer or segment whatever the other lists say.
 */
const PriceListEditor: React.FC<{ owner: string; title: string; subtitle?: string; fallbacks?: PriceFallback[] }> = ({ owner, title, subtitle, fallbacks = [] }) => {
    const queryClient = useQueryClient();
    const canHide = owner !== 'DEFAULT';
    const { data: products = [] } = useQuery({ queryKey: ['b2b-products'], queryFn: listB2bProducts, staleTime: 5 * 60 * 1000 });
    const { data: prices = [], isLoading } = useQuery({ queryKey: ['b2b-prices', owner], queryFn: () => listB2bPrices(owner) });
    const fallbackLists = useQueries({ queries: fallbacks.map((f) => ({ queryKey: ['b2b-prices', f.owner], queryFn: () => listB2bPrices(f.owner) })) });
    const [edits, setEdits] = useState<Record<string, number | ''>>({});
    const [search, setSearch] = useState('');
    const [filter, setFilter] = useState<FilterValue>({});
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState<string | null>(null);
    const [error, setError] = useState<string | null>(null);
    const [csvCheck, setCsvCheck] = useState<{ file: File; result: PriceCsvResult } | null>(null);

    const byProduct = useMemo(() => new Map(prices.map((p) => [p.productId, p])), [prices]);
    // The price the product would get from the lists below this one; a hide further down ends the search.
    const inheritedOf = (productId: string): { price: number; from: string } | null => {
        for (const [i, query] of fallbackLists.entries()) {
            const row = query.data?.find((p) => p.productId === productId);
            // No row, or a cleared one (no price, not hidden): the next list decides.
            if (!row || (!row.hidden && row.price === null)) continue;
            return !row.hidden && row.price !== null ? { price: row.price, from: fallbacks[i]?.label ?? '' } : null;
        }
        return null;
    };
    const withPrices = (p: B2bProduct): PriceRow => ({ ...p, current: byProduct.get(p.productId) ?? null, inherited: inheritedOf(p.productId) });

    /**
     * The whole list as a CSV the import reads back: edit price / offered and upload it as it is.
     * Only sku, price and offered are read on import; the other columns are for reference.
     */
    const download = () => {
        const cell = (v: string | number | null | undefined) => {
            const text = v === null || v === undefined ? '' : String(v);
            return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
        };
        const header = ['sku', 'name', 'category', 'mrp', 'price', ...(canHide ? ['offered'] : []), 'price_now', 'price_from'];
        const lines = products.map(withPrices).map((r) => {
            const own = r.current?.price ?? null;
            const now = r.current?.hidden ? null : own ?? r.inherited?.price ?? null;
            const from = r.current?.hidden ? 'Not offered' : own !== null ? 'This list' : r.inherited?.from ?? 'Not priced';
            return [r.sku, r.name, r.category, r.mrp, own, ...(canHide ? [r.current?.hidden ? 'N' : 'Y'] : []), now, from].map(cell).join(',');
        });
        const slug = title.replace(/[^A-Za-z0-9]+/g, '-').replace(/^-|-$/g, '').toLowerCase();
        downloadCsv([header.join(','), ...lines].join('\n'), `${slug}-${new Date().toISOString().slice(0, 10)}.csv`);
    };

    // The Product filter other grids use: category, size, colour, and whether this list sets a price.
    const filterParams = useMemo<FilterParam[]>(
        () => [
            ...productFilterParams(products),
            {
                key: 'priced',
                label: 'Priced on this list',
                mode: 'single',
                options: [
                    { label: 'Priced', value: 'yes' },
                    { label: 'Not priced', value: 'no' },
                ],
            },
        ],
        [products],
    );
    const pricedFilter = filter.priced?.[0];
    const needle = search.trim().toLowerCase();
    const rows: PriceRow[] = products
        .filter((p) => !needle || p.name.toLowerCase().includes(needle) || p.sku.toLowerCase().includes(needle))
        .filter((p) => matchesProductFilter(p, filter))
        .filter((p) => !pricedFilter || (pricedFilter === 'yes') === (byProduct.get(p.productId)?.price != null))
        .map(withPrices);

    const changed = Object.entries(edits).filter(([id, v]) => v !== '' && Number(v) !== byProduct.get(id)?.price);
    const priced = prices.filter((p) => p.price !== null).length;
    const notOffered = prices.filter((p) => p.hidden).length;
    const refresh = () => Promise.all([queryClient.invalidateQueries({ queryKey: ['b2b-prices'] }), queryClient.invalidateQueries({ queryKey: ['b2b-catalog'] })]);

    const run = async (action: () => Promise<string | null>) => {
        setBusy(true);
        setError(null);
        setMessage(null);
        try {
            setMessage(await action());
            await refresh();
        } catch (e) {
            setError(errorMessage(e));
        } finally {
            setBusy(false);
        }
    };

    const columns: DataSheetColumn<PriceRow>[] = [
        { key: 'name', header: 'Product', width: 280, sticky: true, render: (_v, r) => <ProductCell name={r.name} sku={r.sku} /> },
        { key: 'category', header: 'Category', width: 140, render: (v: string | null) => v ?? '—' },
        { key: 'mrp', header: 'MRP', width: 100, align: 'right', render: (v: number | null) => money(v) },
        {
            key: 'price',
            header: 'Price (before GST)',
            width: 160,
            render: (_v, r) => (
                <NumberInput
                    size="xs"
                    min={0}
                    decimalScale={2}
                    className={styles.priceInput}
                    placeholder={r.inherited ? `${money(r.inherited.price)}` : 'Not set'}
                    value={edits[r.productId] ?? r.current?.price ?? ''}
                    onChange={(v) => setEdits((e) => ({ ...e, [r.productId]: v === '' ? '' : Number(v) }))}
                />
            ),
        },
        {
            key: 'from',
            header: 'Price from',
            width: 140,
            render: (_v, r) =>
                r.current?.price !== null && r.current?.price !== undefined ? 'This list' : <span className={styles.muted}>{r.inherited?.from ?? 'Not priced'}</span>,
        },
        ...(canHide
            ? [
                  {
                      key: 'offered',
                      header: 'Offered',
                      width: 110,
                      render: (_v: unknown, r: PriceRow) =>
                          // Nothing to switch off when no list prices it: it is not offered anyway.
                          !r.current?.hidden && r.current?.price == null && !r.inherited ? (
                              <span className={styles.muted}>—</span>
                          ) : (
                              <Switch
                                  size="xs"
                                  checked={!r.current?.hidden}
                                  disabled={busy}
                                  label={r.current?.hidden ? 'No' : 'Yes'}
                                  onChange={(e) => {
                                      const offered = e.currentTarget.checked;
                                      void run(async () => {
                                          await setB2bPricesOffered(owner, [r.productId], offered);
                                          return `${r.sku} ${offered ? 'offered again' : 'is no longer offered'}`;
                                      });
                                  }}
                              />
                          ),
                  } satisfies DataSheetColumn<PriceRow>,
              ]
            : []),
        { key: 'since', header: 'Since', width: 110, render: (_v, r) => formatDay(r.current?.priceEffectiveFrom) },
        {
            key: 'history',
            header: 'Earlier',
            width: 120,
            render: (_v, r) =>
                r.current?.priceHistory.length ? (
                    <Popover withArrow shadow="md">
                        <Popover.Target>
                            <Button size="xs" variant={ButtonVariant.SUBTLE}>
                                {r.current.priceHistory.length} change{r.current.priceHistory.length > 1 ? 's' : ''}
                            </Button>
                        </Popover.Target>
                        <Popover.Dropdown>
                            <div className={styles.stack}>
                                {[...r.current.priceHistory].reverse().map((h, i) => (
                                    <span key={i} className={styles.cardHint}>
                                        {h.hidden ? 'Not offered' : h.price === null ? 'No price on this list' : money(Number(h.price))}
                                        {h.hidden && h.price !== null ? ` (${money(Number(h.price))})` : ''} · {formatDay(h.from)} to {formatDay(h.to)}
                                        {h.by ? ` · set by ${h.by}` : ''}
                                    </span>
                                ))}
                            </div>
                        </Popover.Dropdown>
                    </Popover>
                ) : (
                    '—'
                ),
        },
        {
            key: 'remove',
            header: '',
            width: 90,
            render: (_v, r) =>
                r.current?.price !== null && r.current?.price !== undefined ? (
                    <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={() => void run(async () => (await removeB2bPrice(owner, r.productId), `Removed the price of ${r.sku}`))}>
                        Clear
                    </Button>
                ) : null,
        },
    ];

    return (
        <div className={styles.stack}>
            <DataSheet<PriceRow>
                title={title}
                subtitle={subtitle ?? `${priced} of ${products.length} products priced on this list${notOffered ? ` · ${notOffered} not offered` : ''}`}
                data={rows}
                columns={columns}
                getRowKey={(r) => r.productId}
                isLoading={isLoading}
                enableSearch
                searchPlaceholder="Search name or SKU"
                searchValue={search}
                onSearchChange={setSearch}
                filters={[{ label: 'Product', params: filterParams, value: filter, onChange: setFilter }]}
                maxHeight="auto"
                toolbarControls={
                    <div className={styles.row}>
                        <Button size="xs" variant={ButtonVariant.OUTLINED} disabled={products.length === 0} onClick={download}>
                            Download CSV
                        </Button>
                        <FileButton
                            accept=".csv,text/csv"
                            onChange={(file) => {
                                if (!file) return;
                                void run(async () => {
                                    const result = await uploadB2bPrices(owner, file, true);
                                    setCsvCheck({ file, result });
                                    return null;
                                });
                            }}
                        >
                            {(props) => (
                                <Button {...props} size="xs" variant={ButtonVariant.OUTLINED}>
                                    Import CSV
                                </Button>
                            )}
                        </FileButton>
                        <Button
                            size="xs"
                            variant={ButtonVariant.ACTION}
                            disabled={changed.length === 0}
                            loading={busy}
                            onClick={() =>
                                void run(async () => {
                                    const r = await upsertB2bPrices(owner, changed.map(([productId, price]) => ({ productId, price: Number(price) })));
                                    setEdits({});
                                    return `Saved: ${r.created} new, ${r.updated} changed`;
                                })
                            }
                        >
                            Save {changed.length || ''} price{changed.length === 1 ? '' : 's'}
                        </Button>
                    </div>
                }
            />
            {csvCheck ? (
                <div className={styles.card}>
                    <span className={styles.cardTitle}>Check {csvCheck.file.name}</span>
                    <span className={styles.cardHint}>
                        {csvCheck.result.totalRows} rows: {csvCheck.result.created} new, {csvCheck.result.updated} changed, {csvCheck.result.skipped} unchanged or skipped.
                        Read from the file: sku (or product_id), price{canHide ? ' and offered (Y or N)' : ''}. A blank price leaves this list's price as it is; other columns are ignored.
                    </span>
                    {csvCheck.result.errors.slice(0, 10).map((e) => (
                        <span key={`${e.row}:${e.reason}`} className={styles.error}>
                            Row {e.row}
                            {e.sku ? ` (${e.sku})` : ''}: {e.reason}
                        </span>
                    ))}
                    <div className={styles.row}>
                        <Button
                            size="xs"
                            variant={ButtonVariant.ACTION}
                            loading={busy}
                            disabled={csvCheck.result.errors.length > 0}
                            onClick={() =>
                                void run(async () => {
                                    const r = await uploadB2bPrices(owner, csvCheck.file, false);
                                    setCsvCheck(null);
                                    return `Imported: ${r.created} new, ${r.updated} changed`;
                                })
                            }
                        >
                            Import
                        </Button>
                        <Button size="xs" variant={ButtonVariant.SUBTLE} onClick={() => setCsvCheck(null)}>
                            Discard
                        </Button>
                    </div>
                </div>
            ) : null}
            {message ? <span className={styles.cardHint}>{message}</span> : null}
            {error ? <span className={styles.error}>{error}</span> : null}
        </div>
    );
};

export default PriceListEditor;
